import { type CheckoutTheme, DEFAULT_CHECKOUT_THEME, isValidCheckoutTheme } from '@getjust/leaf';

export interface ThemeColors {
  // Text colors
  text: string;
  textSecondary: string;
  textMuted: string;

  // Background colors
  bg: string;
  bgSecondary: string;
  bgTertiary: string;
  cardBg: string;

  // Border colors
  border: string;
  borderSecondary: string;

  // Input colors
  inputBg: string;
  inputBorder: string;
  inputFocusBorder: string;
  inputDisabledBg: string;
  inputDisabledBorder: string;

  // Button colors
  buttonPrimaryBg: string;
  buttonPrimaryText: string;
  buttonSecondaryBg: string;
  buttonSecondaryText: string;
  buttonDisabledBg: string;
  buttonDisabledText: string;

  // Status colors
  danger: string;
  success: string;
  warning: string;

  // Overlay colors
  overlayBg: string;

  // Skeleton colors
  skeletonBg: string;
  skeletonHighlight: string;
}

/**
 * Theme colors configuration object
 */
const themeColorsMap: Record<CheckoutTheme, ThemeColors> = {
  aurora: {
    // Text colors
    text: '#1C1C1C',
    textSecondary: '#585858',
    textMuted: '#8F8F8F',

    // Background colors
    bg: '#ffffff',
    bgSecondary: '#F7F7F7',
    bgTertiary: '#E0E0E0',
    cardBg: '#ffffff',

    // Border colors
    border: '#E0E0E0',
    borderSecondary: '#F5F5F5',

    // Input colors
    inputBg: '#ffffff',
    inputBorder: '#E0E0E0',
    inputFocusBorder:
      'linear-gradient(90deg, #8DE690 2.08%, #A5F0A3 18.23%, #A6ECD7 33.33%, #97DADA 51.56%, #B3DAE7 67.71%, #B6C9EC 83.33%, #879ACB 100%)',
    inputDisabledBg: '#F7F7F7',
    inputDisabledBorder: '#E0E0E0',

    // Button colors
    buttonPrimaryBg: '#1C1C1C',
    buttonPrimaryText: '#ffffff',
    buttonSecondaryBg: '#F7F7F7',
    buttonSecondaryText: '#585858',
    buttonDisabledBg: '#F7F7F7',
    buttonDisabledText: '#8F8F8F',

    // Status colors
    danger: '#EE5829',
    success: '#63DD51',
    warning: '#FFA908',

    // Overlay colors
    overlayBg: 'rgba(255, 255, 255, 0.8)',

    // Skeleton colors
    skeletonBg: '#F7F7F7',
    skeletonHighlight: '#E0E0E0',
  },
  black: {
    // Text colors
    text: '#FFFFFF',
    textSecondary: '#B0B0B0',
    textMuted: '#8F8F8F',

    // Background colors
    bg: '#1C1C1C',
    bgSecondary: '#262323',
    bgTertiary: '#3D3D3D',
    cardBg: '#262323',

    // Border colors
    border: '#585858',
    borderSecondary: '#3D3D3D',

    // Input colors
    inputBg: '#262323',
    inputBorder: '#585858',
    inputFocusBorder: '#FFFFFF',
    inputDisabledBg: '#1C1C1C',
    inputDisabledBorder: '#3D3D3D',

    // Button colors
    buttonPrimaryBg: '#FFFFFF',
    buttonPrimaryText: '#1C1C1C',
    buttonSecondaryBg: '#3D3D3D',
    buttonSecondaryText: '#FFFFFF',
    buttonDisabledBg: '#3D3D3D',
    buttonDisabledText: '#8F8F8F',

    // Status colors
    danger: '#FF6B6B',
    success: '#88F878',
    warning: '#FFA908',

    // Overlay colors
    overlayBg: 'rgba(0, 0, 0, 0.8)',

    // Skeleton colors
    skeletonBg: '#3D3D3D',
    skeletonHighlight: '#585858',
  },
  glassy: {
    // Text colors
    text: '#111111',
    textSecondary: '#2F2F2F',
    textMuted: '#5F5F5F',

    // Background colors
    bg: 'transparent',
    bgSecondary: 'rgba(255, 255, 255, 0.7)',
    bgTertiary: 'rgba(247, 247, 247, 0.7)',
    cardBg: 'rgba(255, 255, 255, 0.7)',

    // Border colors
    border: 'rgba(224, 224, 224, 0.6)',
    borderSecondary: 'rgba(224, 224, 224, 0.4)',

    // Input colors
    inputBg: 'rgba(255, 255, 255, 0.7)',
    inputBorder: 'rgba(224, 224, 224, 0.6)',
    inputFocusBorder: 'rgba(255, 255, 255, 0.9)',
    inputDisabledBg: 'rgba(247, 247, 247, 0.5)',
    inputDisabledBorder: 'rgba(224, 224, 224, 0.4)',

    // Button colors
    buttonPrimaryBg: 'rgba(28, 28, 28, 0.9)',
    buttonPrimaryText: '#ffffff',
    buttonSecondaryBg: 'rgba(224, 224, 224, 0.6)',
    buttonSecondaryText: '#585858',
    buttonDisabledBg: 'rgba(224, 224, 224, 0.4)',
    buttonDisabledText: '#8F8F8F',

    // Status colors
    danger: '#EE5829',
    success: '#63DD51',
    warning: '#FFA908',

    // Overlay colors
    overlayBg: 'rgba(255, 255, 255, 0.8)',

    // Skeleton colors
    skeletonBg: 'rgba(224, 224, 224, 0.3)',
    skeletonHighlight: 'rgba(255, 255, 255, 0.5)',
  },
  soft: {
    // Text colors
    text: '#000000',
    textSecondary: '#555555',
    textMuted: '#888888',

    // Background colors
    bg: '#DEDCDC',
    bgSecondary: '#F0F0F0',
    bgTertiary: '#E0E0E0',
    cardBg: 'transparent',

    // Border colors
    border: '#E0E0E0',
    borderSecondary: '#F0F0F0',

    // Input colors
    inputBg: '#F0F0F0',
    inputBorder: '#E0E0E0',
    inputFocusBorder: '#BEBBB4',
    inputDisabledBg: '#F7F7F7',
    inputDisabledBorder: '#E0E0E0',

    // Button colors
    buttonPrimaryBg: '#F0F0F0',
    buttonPrimaryText: '#888888',
    buttonSecondaryBg: '#F0F0F0',
    buttonSecondaryText: '#000000',
    buttonDisabledBg: '#F0F0F0',
    buttonDisabledText: '#888888',

    // Status colors
    danger: '#EE5829',
    success: '#63DD51',
    warning: '#FFA908',

    // Overlay colors
    overlayBg: 'rgba(255, 255, 255, 0.8)',

    // Skeleton colors
    skeletonBg: '#F0F0F0',
    skeletonHighlight: '#E0E0E0',
  },
  classic: {
    // Text colors
    text: '#000000',
    textSecondary: '#666666',
    textMuted: '#999999',

    // Background colors
    bg: '#FFFFFF',
    bgSecondary: '#F5F5F5',
    bgTertiary: '#E0E0E0',
    cardBg: '#FFFFFF',

    // Border colors
    border: '#E0E0E0',
    borderSecondary: '#F5F5F5',

    // Input colors
    inputBg: '#FFFFFF',
    inputBorder: '#E0E0E0',
    inputFocusBorder: '#000000',
    inputDisabledBg: '#F5F5F5',
    inputDisabledBorder: '#E0E0E0',

    // Button colors
    buttonPrimaryBg: '#E0E0E0',
    buttonPrimaryText: '#555555',
    buttonSecondaryBg: '#E0E0E0',
    buttonSecondaryText: '#000000',
    buttonDisabledBg: '#E0E0E0',
    buttonDisabledText: '#999999',

    // Status colors
    danger: '#EE5829',
    success: '#63DD51',
    warning: '#FFA908',

    // Overlay colors
    overlayBg: 'rgba(255, 255, 255, 0.8)',

    // Skeleton colors
    skeletonBg: '#F5F5F5',
    skeletonHighlight: '#E0E0E0',
  },
  custom: {
    text: '#000000',
    textSecondary: '#666666',
    textMuted: '#999999',
    bg: '#FFFFFF',
    bgSecondary: '#FFFFFF',
    bgTertiary: '#E0E0E0',
    cardBg: '#FFFFFF',
    border: '#E0E0E0',
    borderSecondary: '#F5F5F5',
    inputBg: '#FFFFFF',
    inputBorder: '#E0E0E0',
    inputFocusBorder: '#000000',
    inputDisabledBg: '#F5F5F5',
    inputDisabledBorder: '#E0E0E0',
    buttonPrimaryBg: '#E0E0E0',
    buttonPrimaryText: '#555555',
    buttonSecondaryBg: '#E0E0E0',
    buttonSecondaryText: '#000000',
    buttonDisabledBg: '#E0E0E0',
    buttonDisabledText: '#999999',
    danger: '#EE5829',
    success: '#000000',
    warning: '#FFA908',
    overlayBg: 'rgba(255, 255, 255, 0.8)',
    skeletonBg: '#F5F5F5',
    skeletonHighlight: '#E0E0E0',
  },
};

/**
 * Get theme colors based on the checkout theme name
 * @param themeName - The checkout theme name (aurora, black, or glassy)
 * @returns An object containing all theme-specific colors
 */
export const getThemeColors = (themeName: CheckoutTheme): ThemeColors => {
  return themeColorsMap[themeName] ?? themeColorsMap.aurora;
};

/**
 * Helper function to get a theme color with fallback
 * @param themeName - The checkout theme name
 * @param colors - Object with theme colors: { aurora: string, black: string, glassy: string, soft: string, classic: string }
 * @returns The appropriate color for the theme
 */
export const getThemeColor = (
  themeName: CheckoutTheme,
  colors: { aurora: string; black: string; glassy: string; soft: string; classic: string; custom: string },
): string => {
  return colors[themeName] ?? colors.aurora;
};

export interface ScrollbarStyles {
  '&::-webkit-scrollbar': { width: string };
  '&::-webkit-scrollbar-track': { background: string };
  '&::-webkit-scrollbar-thumb': { background: string; borderRadius: string };
  scrollbarColor: string;
  scrollbarWidth: string;
}

/**
 * Get scrollbar styles based on the checkout theme name
 * @param themeName - The checkout theme name
 * @returns Scrollbar CSS styles object or undefined if theme uses default scrollbar
 */
export const getScrollbarStyles = (themeName: CheckoutTheme): ScrollbarStyles | undefined => {
  if (themeName === 'soft') {
    return {
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#F0F0F0',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#D0D0D0',
        borderRadius: '3px',
      },
      scrollbarColor: '#D0D0D0 #F0F0F0',
      scrollbarWidth: 'thin',
    };
  }
  if (themeName === 'classic' || themeName === 'custom') {
    return {
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#F5F5F5',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#D0D0D0',
        borderRadius: '3px',
      },
      scrollbarColor: '#D0D0D0 #F5F5F5',
      scrollbarWidth: 'thin',
    };
  }
  return undefined;
};

/**
 * A minimal type shape for an object containing theme settings.
 * This avoids a circular dependency on the full Shop type from the useShop hook.
 */
interface ThemedObject {
  settings?: {
    checkoutTheme?: string;
  };
}

/**
 * Get the effective checkout theme from a shop or themed object.
 * Returns the configured theme if valid, otherwise falls back to the default theme.
 * @param entity - An object with theme settings (e.g., shop)
 * @returns The effective CheckoutTheme
 */
export const getEffectiveCheckoutTheme = (entity?: ThemedObject | null): CheckoutTheme => {
  const shopTheme = entity?.settings?.checkoutTheme;
  if (shopTheme && isValidCheckoutTheme(shopTheme)) {
    return shopTheme;
  }

  return DEFAULT_CHECKOUT_THEME;
};
